import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { Label } from "reactstrap";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import { validateCPF } from "../../../../../../helpers/format";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { ITerminal } from "../../../../Cadastro/Terminal/Create/types/types";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import * as Yup from 'yup';
import { OperationType } from "../../../OperacoesPortoAgendada/types/types";
import { IOperacaoAgendadaSPE } from "../../types/types";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IOperacaoAgendadaSPE;
  onConfirm: () => void;
  onClose: () => void;
}

interface FormValues {
  data_agendamento_terminal: string;
  placa_dianteira_veiculo: string;
  placa_traseira_veiculo: string;
  identificadores_conteineres: string;
  tipo_carga: string;
  tipo_operacao: string;
  id_terminal: string;
  cpf_motorista: string;
  cnpj_transportadora: string;
}

const validationSchemaSPE = Yup.object().shape({
  data_agendamento_terminal: Yup.string().required('Este campo é obrigatório!'),
  placa_dianteira_veiculo: Yup.string().required('Este campo é obrigatório!'),
  placa_traseira_veiculo: Yup.string().notRequired(),
  tipo_carga: Yup.string().required('Este campo é obrigatório!'),
  tipo_operacao: Yup.string().required('Este campo é obrigatório!'),
  id_terminal: Yup.string().required('Este campo é obrigatório!'),
  cpf_motorista: Yup.string().required('Este campo é obrigatório!'),
});

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [terminais, setTerminal] = useState<any[]>([]);
  const [cargoTypes, setCargoTypes] = useState<any[]>([]);
  const [operationTypes, setOperationTypes] = useState<any[]>([]);
  const { user } = useAuth();
  const [conteiners, setConteiners] = useState({
    conteiners1: "",
    conteiners2: "",
    conteiners3: "",
    conteiners4: "",
  });

  const handleSubmit = useCallback(
    async (
      values: FormValues,
      row?: IOperacaoAgendadaSPE,
      listConteiners?: any
    ) => {
      try {
        setLoading(true);

        const userId = user?.id;

        let getIndentificadorConteiner = "{";

        if (listConteiners?.conteiners1?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners1},`;
        }
        if (listConteiners?.conteiners2?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners2},`;
        }
        if (listConteiners?.conteiners3?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners3},`;
        }
        if (listConteiners?.conteiners4?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners4},`;
        }

        getIndentificadorConteiner += "}";

        const conteinersStr = getIndentificadorConteiner.length > 3
          ? getIndentificadorConteiner.replace(",}", "}").replaceAll("-", "").toUpperCase()
          : null;

        if (validateCPF(values.cpf_motorista)) {
          let body: any = {
            cnpj_transportadora: values.cnpj_transportadora && values.cnpj_transportadora.length > 0
              ? values.cnpj_transportadora.replaceAll('.', '').replaceAll('-', '').replace('/', '')
              : null,
            data_agendamento_terminal: values.data_agendamento_terminal
              ? format(
                  new Date(values.data_agendamento_terminal),
                  "yyyy-MM-dd HH:mm:ss",
                  { locale: ptBR }
                )
              : null,
            cpf_motorista: String(values.cpf_motorista)
              .replaceAll(".", "")
              .replace("-", ""),
            placa_dianteira_veiculo: values.placa_dianteira_veiculo
              ? values.placa_dianteira_veiculo.replace("-", "").toUpperCase()
              : "",
            placa_traseira_veiculo:
              values.placa_traseira_veiculo && values.placa_traseira_veiculo.length > 0
                ? values.placa_traseira_veiculo.replace("-", "").toUpperCase()
                : null,
            tolerancia_inicio_agendamento: null,
            tolerancia_fim_agendamento: null,
            id_terminal: values.id_terminal ? Number(values.id_terminal) : null,
            tipo_carga: values.tipo_carga || null,
            tipo_operacao: values.tipo_operacao ? Number(values.tipo_operacao) : null,
            identificadores_conteineres: conteinersStr,
            ativo: true,
            id_usuario_historico: userId,
            origem: 1,
            status: 0,
          };

          const idAgendamentoSPE = row?.id_operacao_porto_agendada_SPE;

          if (row && idAgendamentoSPE) {
            body = {
              ...body,
              id_operacao_porto_agendada_SPE: idAgendamentoSPE,
            };
          }

          if (props.isEdit) {
            const response = await api.post("/editar/operacaoAgendadaSpe", body).catch(() =>
              api.post("/atualizar/operacaoAgendadaSpe", body)
            );

            if (response.status === 200 || response.data) {
              FrontendNotification("Agendamento SPE atualizado com sucesso!", "success");
              props.onConfirm();
            } else {
              FrontendNotification("Erro ao salvar o agendamento SPE!", "error");
            }
          } else {
            const response = await api.post("/cadastrar/operacaoAgendadaSpe", body);

            if (response.status === 200 || response.data) {
              FrontendNotification("Agendamento SPE cadastrado com sucesso!", "success");
              props.onConfirm();
            } else {
              FrontendNotification("Erro ao salvar o agendamento SPE!", "error");
            }
          }
        } else {
          FrontendNotification("CPF inválido", "error");
        }

        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        const msg = error?.response?.data?.error_msg || "Erro ao salvar o agendamento SPE!";
        FrontendNotification(msg, "error");
      }
    },
    [props, user]
  );

  const getTerminais = useCallback(async () => {
    try {
      setLoading(true);

      const body = {
        order_by: "data_historico",
        order_direction: "desc",
        qtd_por_pagina: 100,
      };

      const response = await api.post("/listar/terminais", body);

      if (response.status === 200) {
        let mappingResponse = response.data.data.map((item: ITerminal) => {
          return {
            label: item.razao_social,
            value: item.id_terminal,
            tipos_carga: item.tipos_carga
          };
        });

        mappingResponse.unshift({
          label: 'Selecione uma opção',
          value: null,
          tipos_carga: null,
        });

        setTerminal(mappingResponse);
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  const getCargoTypesSPE = useCallback(async () => {
    try {
      const response = await api.post("/listar/tipoCargaSpe", {
        qtd_por_pagina: 100,
        order_by: "tipo_carga",
        order_direction: "asc",
      }).catch(() => null);

      let data: any[] = [];
      if (response && response.data) {
        const list = response.data.data || response.data;
        if (Array.isArray(list)) {
          data = list.map((item: any) => ({
            label: item.tipo_carga,
            value: item.tipo_carga,
          }));
        }
      }
      setCargoTypes(data);
    } catch {
      setCargoTypes([]);
    }
  }, []);

  const getOperationTypes = useCallback(() => {
    const data = Object.values(OperationType).map(
      (value: any, index: number) => {
        return {
          value: `${index + 1}`,
          label: value,
        };
      }
    );

    setOperationTypes(data);
  }, []);

  const onLoadFormValues = useCallback((row?: IOperacaoAgendadaSPE) => {
    const data = row;

    if (data) {
      formik.setFieldValue(
        "data_agendamento_terminal",
        data.data_agendamento_terminal
          ? format(new Date(data.data_agendamento_terminal), "yyyy-MM-dd'T'HH:mm")
          : ""
      );
      formik.setFieldValue(
        "placa_dianteira_veiculo",
        data.placa_dianteira_veiculo || ""
      );
      formik.setFieldValue(
        "placa_traseira_veiculo",
        data.placa_traseira_veiculo || ""
      );
      formik.setFieldValue("id_terminal", data.id_terminal ?? "");
      formik.setFieldValue("tipo_carga", data.tipo_carga ?? "");
      formik.setFieldValue("tipo_operacao", data.tipo_operacao ?? "");
      formik.setFieldValue("cpf_motorista", data.cpf_motorista || "");
      formik.setFieldValue("cnpj_transportadora", data.cnpj_transportadora || "");

      if (
        data.identificadores_conteineres !== null &&
        data.identificadores_conteineres !== undefined &&
        data.identificadores_conteineres.length > 0
      ) {
        const getConteineres = data.identificadores_conteineres
          .replace("{", "")
          .replace("}", "");

        const mappingConteiners = getConteineres.split(",");

        setConteiners({
          conteiners1: mappingConteiners[0] ? mappingConteiners[0] : "",
          conteiners2: mappingConteiners[1] ? mappingConteiners[1] : "",
          conteiners3: mappingConteiners[2] ? mappingConteiners[2] : "",
          conteiners4: mappingConteiners[3] ? mappingConteiners[3] : "",
        });
      }
    }
  }, []);

  const initialValues: FormValues = {
    placa_dianteira_veiculo: "",
    placa_traseira_veiculo: "",
    data_agendamento_terminal: "",
    id_terminal: "",
    tipo_carga: "",
    tipo_operacao: "",
    identificadores_conteineres: "",
    cpf_motorista: "",
    cnpj_transportadora: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchemaSPE,
    onSubmit: (values: FormValues) => {
      handleSubmit(values, props.selectedRow, conteiners);
    },
  });

  useEffect(() => {
    getTerminais();
    getCargoTypesSPE();
    getOperationTypes();
  }, [getTerminais, getCargoTypesSPE, getOperationTypes]);

  useEffect(() => {
    if (props.isView || props.isEdit) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={12} skeletonColumns={3} />
      <ToastContainer />
      <div className="overflow-y-auto max-h-[calc(70vh)] p-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <InputCustom
              id="data_agendamento_terminal"
              title="Data Hora"
              type="datetime-local"
              placeholder=""
              onChange={formik.handleChange("data_agendamento_terminal")}
              value={formik.values.data_agendamento_terminal}
              touched={formik.touched.data_agendamento_terminal}
              error={formik.errors.data_agendamento_terminal}
              disabled={props.isView}
            />
          </div>
          <div>
            <SelectCustom
              id="id_terminal"
              data={terminais}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("id_terminal", selectedOption.value);
              }}
              title="Terminal"
              touched={formik.touched.id_terminal}
              error={formik.errors.id_terminal}
              disabled={props.isView}
              value={formik.values.id_terminal}
            />
          </div>
          <div>
            <SelectCustom
              id="tipo_operacao"
              data={operationTypes}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("tipo_operacao", selectedOption.value);
              }}
              title="Tipo de Operação"
              touched={formik.touched.tipo_operacao}
              error={formik.errors.tipo_operacao}
              disabled={props.isView}
              value={formik.values.tipo_operacao}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <SelectCustom
              id="tipo_carga"
              data={cargoTypes}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("tipo_carga", selectedOption.value);
              }}
              title="Tipo de Carga"
              touched={formik.touched.tipo_carga}
              error={formik.errors.tipo_carga}
              disabled={props.isView}
              value={formik.values.tipo_carga}
            />
          </div>
          <div>
            <InputCustom
              id="cpf_motorista"
              title="CPF Motorista"
              typeInput="mask"
              mask="999.999.999-99"
              placeholder=""
              onChange={formik.handleChange("cpf_motorista")}
              value={formik.values.cpf_motorista}
              touched={formik.touched.cpf_motorista}
              error={formik.errors.cpf_motorista}
              disabled={props.isView}
            />
          </div>
          <div>
            <InputCustom
              id="cnpj_transportadora"
              title="CNPJ Transportadora"
              typeInput="mask"
              mask="99.999.999/9999-99"
              placeholder=""
              onChange={formik.handleChange("cnpj_transportadora")}
              value={formik.values.cnpj_transportadora}
              touched={formik.touched.cnpj_transportadora}
              error={formik.errors.cnpj_transportadora}
              disabled={props.isView}
              isRequired={false}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <InputCustom
              id="placa_dianteira_veiculo"
              title="Placa Dianteira Veículo"
              typeInput="mask"
              mask="aaa-9*99"
              placeholder=""
              onChange={formik.handleChange("placa_dianteira_veiculo")}
              value={formik.values.placa_dianteira_veiculo}
              touched={formik.touched.placa_dianteira_veiculo}
              error={formik.errors.placa_dianteira_veiculo}
              disabled={props.isView}
            />
          </div>
          <div>
            <InputCustom
              id="placa_traseira_veiculo"
              title="Placa Traseira Veiculo"
              typeInput="mask"
              mask="aaa-9*99"
              placeholder=""
              onChange={formik.handleChange("placa_traseira_veiculo")}
              value={formik.values.placa_traseira_veiculo}
              touched={formik.touched.placa_traseira_veiculo}
              error={formik.errors.placa_traseira_veiculo}
              disabled={props.isView}
              isRequired={false}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 mb-2">
          <div>
            <Label
              style={{
                fontFamily: "Poppins",
                fontSize: "0.875rem",
                color: "#000000",
                fontWeight: 500,
                display: "flex",
                flexDirection: "column",
                marginBottom: "0.400rem !important",
              }}
            >
              Identificadores dos Contêineres
            </Label>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <InputCustom
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners1: value,
                    })
                  }
                  value={conteiners.conteiners1}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="conteiners2"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners2: value,
                    })
                  }
                  value={conteiners.conteiners2}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="conteiners3"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners3: value,
                    })
                  }
                  value={conteiners.conteiners3}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="conteiners4"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners4: value,
                    })
                  }
                  value={conteiners.conteiners4}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
          onClick={() => formik.handleSubmit()}
          id="btnSave"
        >
          Salvar
        </button>
      </div>
    </>
  );
};

export default Form;
